import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supportApi } from '../services/api';
import { formatDistanceToNow } from 'date-fns';

interface SupportTicket {
  id: string;
  subject: string;
  description: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'AWAITING_RESPONSE' | 'RESOLVED' | 'CLOSED';
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  ticketType: 'PROBLEM' | 'FEATURE_REQUEST';
  createdAt: string;
  updatedAt: string;
}

const STATUS_COLORS: Record<string, string> = {
  OPEN: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  AWAITING_RESPONSE: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
  RESOLVED: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  CLOSED: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

const PRIORITY_COLORS: Record<string, string> = {
  LOW: 'text-gray-500',
  NORMAL: 'text-blue-500',
  HIGH: 'text-orange-500',
  URGENT: 'text-red-500',
};

const TICKET_TYPE_BADGES: Record<string, string> = {
  PROBLEM: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  FEATURE_REQUEST: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
};

const TICKET_TYPE_LABELS: Record<string, string> = {
  PROBLEM: 'Problem',
  FEATURE_REQUEST: 'Feature Request',
};

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_FILES = 3;

export default function SupportTickets() {
  const queryClient = useQueryClient();

  // Form state
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<'LOW' | 'NORMAL' | 'HIGH' | 'URGENT'>('NORMAL');
  const [ticketType, setTicketType] = useState<'PROBLEM' | 'FEATURE_REQUEST'>('PROBLEM');
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null!);

  // Ticket detail modal
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);

  // Fetch ticket list
  const { data: tickets, isLoading } = useQuery({
    queryKey: ['support-tickets'],
    queryFn: async () => {
      const response = await supportApi.listTickets();
      return response.data as SupportTicket[];
    },
  });

  const createTicketMutation = useMutation({
    mutationFn: (data: { subject: string; description: string; priority: string; ticketType: string }) =>
      supportApi.createTicket(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
      setSubject('');
      setDescription('');
      setPriority('NORMAL');
      setTicketType('PROBLEM');
      setFiles([]);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 5000);
    },
    onError: (err) => {
      setError((err as Error).message || 'Failed to create ticket');
    },
  });

  const handleFileSelect = (selected: FileList | null) => {
    if (!selected) return;
    const next: File[] = [];
    for (let i = 0; i < selected.length && files.length + next.length < MAX_FILES; i++) {
      const f = selected[i];
      if (!ALLOWED_TYPES.includes(f.type)) {
        setError(`${f.name}: Invalid type. Use JPEG, PNG, GIF, or WebP.`);
        continue;
      }
      if (f.size > MAX_FILE_SIZE) {
        setError(`${f.name}: Too large. Maximum 5MB.`);
        continue;
      }
      next.push(f);
    }
    setFiles((prev) => [...prev, ...next]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!subject.trim()) {
      setError('Please enter a subject');
      return;
    }
    if (!description.trim()) {
      setError('Please describe your issue');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await createTicketMutation.mutateAsync({
        subject: subject.trim(),
        description: description.trim(),
        priority,
        ticketType,
      });

      // Upload screenshots after ticket is created
      if (files.length > 0 && result?.data?.id) {
        const ticketId = result.data.id;
        // Wait briefly for Zoho ticket to be created (fire-and-forget on backend)
        await new Promise((r) => setTimeout(r, 2000));
        for (const file of files) {
          try {
            await supportApi.uploadScreenshot(ticketId, file);
          } catch {
            // Screenshot upload failure is non-fatal
          }
        }
      }

      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Link
        to="/app/support"
        className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-6"
      >
        <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Support
      </Link>

      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Support Tickets</h1>

      {/* Create Ticket Form */}
      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Create Support Ticket
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg text-sm">
            Ticket submitted! We'll reply to your registered email address.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="input"
              placeholder="Brief description of your issue"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Type
            </label>
            <select
              value={ticketType}
              onChange={(e) => {
                setTicketType(e.target.value as typeof ticketType);
                if (e.target.value === 'FEATURE_REQUEST') setPriority('NORMAL');
              }}
              className="input"
            >
              <option value="PROBLEM">Problem - Something isn't working</option>
              <option value="FEATURE_REQUEST">Feature Request - I'd like something new</option>
            </select>
          </div>

          {ticketType === 'PROBLEM' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Priority
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as typeof priority)}
                className="input"
              >
                <option value="LOW">Low - General question</option>
                <option value="NORMAL">Normal - Issue affecting usage</option>
                <option value="HIGH">High - Major functionality broken</option>
                <option value="URGENT">Urgent - Data loss or security issue</option>
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input min-h-[120px]"
              placeholder="Please describe your issue in detail..."
            />
          </div>

          {/* Screenshot attachment (plain upload) */}
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              multiple
              className="hidden"
              onChange={(e) => {
                handleFileSelect(e.target.files);
                e.target.value = '';
              }}
            />
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {files.map((file, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-900/30 rounded text-xs text-blue-700 dark:text-blue-300"
                  >
                    <span className="max-w-[100px] truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => setFiles(files.filter((_, j) => j !== i))}
                      className="text-blue-500 hover:text-blue-700 ml-1"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
            {files.length < MAX_FILES && (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                + Attach screenshot ({MAX_FILES - files.length} remaining)
              </button>
            )}
          </div>

          <button type="submit" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? 'Submitting...' : 'Submit Ticket'}
          </button>
        </form>
      </div>

      {/* Tickets List */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Your Tickets</h2>

        {isLoading ? (
          <div className="animate-pulse space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded" />
            ))}
          </div>
        ) : tickets && tickets.length > 0 ? (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <div
                key={ticket.id}
                className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                onClick={() => setSelectedTicket(ticket)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900 dark:text-white">{ticket.subject}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                      {ticket.description}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                      Created {formatDistanceToNow(new Date(ticket.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2 ml-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_COLORS[ticket.status]}`}>
                      {ticket.status.replace('_', ' ')}
                    </span>
                    {ticket.ticketType && (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${TICKET_TYPE_BADGES[ticket.ticketType] || ''}`}>
                        {TICKET_TYPE_LABELS[ticket.ticketType] || ticket.ticketType}
                      </span>
                    )}
                    <span className={`text-xs ${PRIORITY_COLORS[ticket.priority]}`}>
                      {ticket.priority}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-center py-8">
            You have no support tickets yet.
          </p>
        )}
      </div>

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {selectedTicket.subject}
                  </h3>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[selectedTicket.status]}`}>
                      {selectedTicket.status.replace('_', ' ')}
                    </span>
                    {selectedTicket.ticketType && (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${TICKET_TYPE_BADGES[selectedTicket.ticketType] || ''}`}>
                        {TICKET_TYPE_LABELS[selectedTicket.ticketType] || selectedTicket.ticketType}
                      </span>
                    )}
                    <span className={`text-xs font-medium ${PRIORITY_COLORS[selectedTicket.priority]}`}>
                      {selectedTicket.priority}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {formatDistanceToNow(new Date(selectedTicket.createdAt), { addSuffix: true })}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedTicket(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 ml-4"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
              {/* Original description */}
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                  {selectedTicket.description}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  You &bull; {new Date(selectedTicket.createdAt).toLocaleString()}
                </p>
              </div>

              {/* Info banner */}
              <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-sm text-amber-800 dark:text-amber-200">
                <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Replies from our support team will be sent to your registered email address.</span>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setSelectedTicket(null)}
                className="btn-secondary w-full"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
